<template>
  <div class="sleep-trend-card">
    <div class="ms-3 mb-4">
        <div class="mb-0 text-sm text-uppercase text-primary font-weight-bold">
            {{ dateRange }}
        </div>
        <h5 id="header" class="mb-0 font-weight-bolder mt-1">
                {{ title }} {{ lastSynInfo }}
        </h5>
    </div>
    <div v-if="sleepFeatures.length > 0" class="chart-wrap">
      <canvas ref="canvasRef"></canvas>
    </div>
    <div v-else>No data found for the selected date range.</div>

    <!-- Summary strip (like the screenshot) -->
    <div v-if="sleepFeatures.length > 0" class="summary">
      <div class="pill-accent"></div>

      <div class="summary-item">
        <div class="k">Sleep Hours</div>
        <div class="v">{{ summary.sleepHoursRange }}</div>
      </div>

      <div class="summary-item">
        <div class="k">Sleep Need</div>
        <div class="v">{{ summary.sleepNeedRange }}</div>
      </div>

      <div class="summary-item">
        <div class="k">Sleep Need (%)</div>
        <div class="v">{{ summary.sleepNeedPctRange }}</div>
      </div>

      <div class="summary-item">
        <div class="k">Sleep Deprivation</div>
        <div class="v">{{ summary.sleepDeprivationRange }}</div>
      </div>

      <div class="summary-item">
        <div class="k">Well-Rested Nights</div>
        <div class="v">{{ summary.wellRested }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, LineController, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler,} from "chart.js";
Chart.register(LineController,LineElement,PointElement,CategoryScale,LinearScale,Tooltip,Legend,Filler);

// ---- helpers ----
function timeToHours(hhmm) {
  // "8:30" -> 8.5
  const [h, m] = String(hhmm).split(":").map((x) => parseInt(x, 10));
  return (h || 0) + ((m || 0) / 60);
}
function hoursToHM(hoursFloat) {
  const totalMin = Math.round((Number(hoursFloat) || 0) * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}
function minutesToHM(minutes) {
  const totalMin = Number(minutes) || 0;
  const h = Math.floor(totalMin / 60);
  const m = Math.round(totalMin % 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

export default {
  name: "SleepTrendChart",
  props: {
    sleepFeatures:{
      type: Array,
      default: () => [],
      required: true,
    },
    title: {
      type: String,
      default: "",
    },
    dateRange: {
      type: String,
      default: "",
    },
    lastSynInfo: {
      type: String,
      default: "",
    },
  },

  data() {
    return { 
        chart: null,
        summary : {
            sleepHoursRange: "",
            sleepNeedRange: "",
            sleepNeedPctRange: "",
            sleepDeprivationRange: "",
            wellRested: "",
        },
        bgColor: "#233334" ,
        gridColor: "rgba(255,255,255,0.08)",
        tickColor: "rgba(255,255,255,0.65)",
        needLineColor: "rgba(220,220,235,0.9)", // grey/white
        hoursLineColor: "#a100ff", // purple
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.renderChart();
      this.generateSummaryFromFeatures(this.sleepFeatures);
    });
    window.addEventListener("resize", this.onResize, { passive: true });
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
    this.destroyChart();
  },

  computed: {
    chartPoints() {
      if (this.sleepFeatures && this.sleepFeatures.length > 0) {
        return this.convertSleepFeaturesToPoints(this.sleepFeatures);
      }
      return [];
    },
  },

  watch: {
    chartPoints: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.generateSummaryFromFeatures(this.sleepFeatures)  
          this.renderChart();
        });
      },
    },
  },

  methods: {
    generateSummaryFromFeatures(features) {
      if (!features || features.length === 0) {
        this.summary =  {
          sleepHoursRange: "N/A",
          sleepNeedRange: "N/A",
          sleepNeedPctRange: "N/A",
          sleepDeprivationRange: "N/A",
          wellRested: "N/A",
        };
      }

      const validFeatures = features.filter(
        (f) => f.sleep_mins !== null && f.sleep_need !== null && f.sleep_need > 0
      );

      if (validFeatures.length === 0) {
        this.summary =  {
          sleepHoursRange: "N/A",
          sleepNeedRange: "N/A",
          sleepNeedPctRange: "N/A",
          sleepDeprivationRange: "N/A",
          wellRested: `0 of ${features.length}d (0%)`,
        };
      }

      const sleepMins = validFeatures.map((f) => f.sleep_mins);
      const sleepNeeds = validFeatures.map((f) => f.sleep_need);
      const sleepNeedPcts = validFeatures.map((f) =>
        (f.sleep_mins > 0 && f.sleep_need )? Math.round((f.sleep_mins / f.sleep_need) * 100) : 0
      );
      const sleepDeprivations = validFeatures
        .map((f) => f.sleep_need - f.sleep_mins)
        .filter((d) => d > 0);

      const minSleepHours = sleepMins.length > 0 ? Math.min(...sleepMins) : 0;
      const maxSleepHours = sleepMins.length > 0 ? Math.max(...sleepMins) : 0;
      const minSleepNeed = sleepNeeds.length > 0 ? Math.min(...sleepNeeds) : 0;
      const maxSleepNeed = sleepNeeds.length > 0 ? Math.max(...sleepNeeds) : 0;
      const minSleepNeedPct = sleepNeedPcts.length > 0 ? Math.min(...sleepNeedPcts) : 0;
      const maxSleepNeedPct = sleepNeedPcts.length > 0 ? Math.max(...sleepNeedPcts) : 0;
      const minSleepDeprivation =
        sleepDeprivations.length > 0 ? Math.min(...sleepDeprivations) : 0;
      const maxSleepDeprivation =
        sleepDeprivations.length > 0 ? Math.max(...sleepDeprivations) : 0;

      const wellRestedDays = validFeatures.filter(
        (f) => f.sleep_mins >= f.sleep_need
      ).length;
      const wellRestedPct = Math.round(
        features.length > 0 ? (wellRestedDays / features.length) * 100 : 0
      );

      this.summary = {
        sleepHoursRange: minSleepHours && maxSleepHours ? `${minutesToHM(minSleepHours)} - ${minutesToHM(
          maxSleepHours
        )}` : "N/A",
        sleepNeedRange: minSleepNeed && maxSleepNeed ? `${minutesToHM(minSleepNeed)} - ${minutesToHM(
          maxSleepNeed
        )}` : "N/A",
        sleepNeedPctRange: minSleepNeedPct && maxSleepNeedPct ? `${minSleepNeedPct} - ${maxSleepNeedPct}%` : "N/A"  ,
        sleepDeprivationRange: minSleepDeprivation && maxSleepDeprivation ? `${minutesToHM(
          minSleepDeprivation
        )} - ${minutesToHM(maxSleepDeprivation)}` : "N/A",
        wellRested: `${wellRestedDays} of ${features.length}d (${wellRestedPct}%)`,
      };
    },
    convertSleepFeaturesToPoints(features) {
      if (!features || features.length === 0) {
        return [];
      }
      return features.map((f) => {
        const parts = f.bucket.split('-').map(p => parseInt(p, 10));
        const bucketDate = new Date(parts[0], parts[1] - 1, parts[2]);

        const sleepDate = bucketDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        const sleepDay = bucketDate.toLocaleDateString("en-US", { weekday: "short" })
          .toUpperCase();

        const sleepHours = f.sleep_mins ? minutesToHM(f.sleep_mins) : "0:00";
        const sleepNeed = f.sleep_need ? minutesToHM(f.sleep_need) : "0:00";

        return {
          sleepDate,
          sleepDay,
          sleepHours,
          sleepNeed,
        };
      });
    },

    onResize() {
      if (this.chart) this.chart.resize();
    },

    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },

    makeValueLabelPlugin() {
      const vm = this;

      return {
        id: "pointValueLabels",
        afterDatasetsDraw(chart) {
          const { ctx } = chart;

          ctx.save();
          ctx.font = "12px sans-serif";
          ctx.textAlign = "center";

          chart.data.datasets.forEach((ds, dsIndex) => {
            const meta = chart.getDatasetMeta(dsIndex);
            if (!meta?.data?.length) return;

            const isNeed = ds.label === "Sleep Need";
            ctx.fillStyle = isNeed ? vm.needLineColor : vm.hoursLineColor;

            meta.data.forEach((pt, i) => {
              const v = ds.data[i];
              const label = hoursToHM(v);
              const y = isNeed ? (pt.y - 10) : (pt.y + 18);
              ctx.textBaseline = isNeed ? "bottom" : "top";
              ctx.fillText(label, pt.x, y);
            });
          });

          ctx.restore();
        },
      };
    },

    renderChart() {
      this.destroyChart();

      if (!this.chartPoints || this.chartPoints.length === 0) {
        return;
      }

      const ctx = this.$refs.canvasRef?.getContext("2d");
      if (!ctx) return;

      const labels = this.chartPoints.map((p) => [p.sleepDate, p.sleepDay]); // 2-line ticks
      const sleepHours = this.chartPoints.map((p) => timeToHours(p.sleepHours));
      const sleepNeed = this.chartPoints.map((p) => timeToHours(p.sleepNeed));

      // Gradient fill under Sleep Hours line
      const grad = ctx.createLinearGradient(0, 0, 0, 260);
      grad.addColorStop(0, "rgba(161,0,255,0.22)");
      grad.addColorStop(1, "rgba(161,0,255,0.00)");

      this.chart = new Chart(ctx, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Sleep Hours",
              data: sleepHours,
              borderColor: this.hoursLineColor,
              backgroundColor: grad,
              fill: true,
              tension: 0.28,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 4,
              pointBackgroundColor: this.hoursLineColor,
              pointBorderColor: this.hoursLineColor,
            },
            {
              label: "Sleep Need",
              data: sleepNeed,
              borderColor: this.needLineColor,
              backgroundColor: "rgba(220,220,235,0.10)",
              fill: false,
              tension: 0.28,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 4,
              pointBackgroundColor: this.needLineColor,
              pointBorderColor: this.needLineColor,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 12, right: 18, bottom: 0, left: 10 } },
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                color: this.tickColor,
                boxWidth: 15,
                boxHeight: 15,
              },
            },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                title: (items) => {
                  const lbl = items?.[0]?.label;
                  if (Array.isArray(lbl)) return `${lbl[0]} ${lbl[1]}`;
                  return String(lbl || "");
                },
                label: (ctx) => `${ctx.dataset.label}: ${hoursToHM(ctx.parsed.y)}`,
              },
            },
          },
          scales: {
            x: {
              ticks: {
                color: this.tickColor,
                font: { size: 11 },
                maxRotation: 0,
                minRotation: 0,
              },
              grid: { 
                display: true,   
                drawBorder: false,
                drawTicks: false,
                color: "rgba(255,255,255,0.06)",
                lineWidth: 1
             },
            },
            y: {
              min: 0,
              max: 12,
              ticks: {
                color: this.tickColor,
                stepSize: 1,
              },
              title: {
                display: true,
                text: "HOURS",
                color: this.tickColor,
                font: { size: 12, weight: "600" },
              },
              grid: { 
                display: false,   
                color: this.gridColor 
              },
            },
          },
        },
        plugins: [this.makeValueLabelPlugin()],
      });
    },
  },
};
</script>

<style scoped>
.sleep-trend-card {
  width: 100%;
  background: var(--bg, #233334);
  border-radius: 12px;
  padding: 14px 14px 10px;
  box-sizing: border-box;
  position: relative;
}

.chart-wrap {
  height: 340px; /* similar aspect as screenshot */
  width: 100%;
}

.summary {
  margin: 14px auto 0;
  max-width: 980px;
  width: 100%;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: 10px repeat(5, 1fr);
  gap: 14px;
  align-items: center;
  box-sizing: border-box;
}

.pill-accent {
  width: 6px;
  height: 100%;
  border-radius: 999px;
  background: #a100ff;
  opacity: 0.9;
  justify-self: start;
}

.summary-item .k {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 4px;
  white-space: nowrap;
}

.summary-item .v {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  font-weight: 600;
  white-space: nowrap;
}

@media (max-width: 900px) {
  .summary {
    grid-template-columns: 10px repeat(2, 1fr);
    row-gap: 10px;
  }
}
</style>
